<template>
  <div id="map"></div>
  <MapLayerControls
    :showGbifData="showGbifData"
    :showOurData="showOurData"
    @update:showGbifData="showGbifData = $event"
    @update:showOurData="showOurData = $event"
  />
</template>

<script lang="ts">
import type { PropType } from 'vue'
import { defineComponent, onMounted, ref, watch } from 'vue'
import { useMetadataStore } from '@/stores/metadataStore'
import TileLayer from 'ol/layer/Tile'
import WMTS, { optionsFromCapabilities } from 'ol/source/WMTS'
import WMTSCapabilities from 'ol/format/WMTSCapabilities'
import type { GeoJSONGeometryCollection } from 'ol/format/GeoJSON'
import GeoJSON from 'ol/format/GeoJSON'
import VectorLayer from 'ol/layer/Vector'
import VectorSource from 'ol/source/Vector'
import { Circle, Fill, RegularShape, Stroke, Style, Text } from 'ol/style'
import { Feature } from 'ol'
import { Geometry } from 'ol/geom'
import { get as getProjection, transform } from 'ol/proj'
import proj4 from 'proj4'
import { register } from 'ol/proj/proj4'
import XYZ from 'ol/source/XYZ'
import router from '@/router'
import Map from 'ol/Map'
import View from 'ol/View'
import { fetchGbifData } from '@/services/GbifService'
import MapLayerControls from '@/components/MapLayerControls.vue'
import { getSpeciesColor } from '@/stores/filtering'

export default defineComponent({
  components: {
    MapLayerControls
  },
  props: {
    geoJSONCollection: Object as PropType<GeoJSONGeometryCollection>
  },
  setup(props) {
    const map = ref<Map | null>(null) // Initialize as null
    const mapView = ref<View | null>(null) // Initialize as null
    const mStore = useMetadataStore()
    const vectorSource = new VectorSource()
    const gbifVectorSource = new VectorSource()
    const styleCache = new globalThis.Map<string, Style>()
    const showGbifData = ref(false)
    const showOurData = ref(true) // Add state for showing original data
    // Define the projections
    proj4.defs(
      'EPSG:21781',
      '+proj=somerc +lat_0=46.95240555555556 +lon_0=7.439583333333333 +k_0=1 +x_0=600000 +y_0=200000 +ellps=bessel +towgs84=674.374,15.056,405.346,0,0,0,0 +units=m +no_defs'
    )
    register(proj4)

    const swissProjection = getProjection('EPSG:21781')

    const getStyle = (speciesGroup: string, zoom: number, sensitiveSpecies: boolean, sampleSize: number): Style => {

      // Check if the styleCache has the key
      const cacheKey = `${speciesGroup}_${sensitiveSpecies}_${sampleSize}`;
      const newRadius = Math.max(4, (zoom - 2));

      const cachedStyle = styleCache.get(cacheKey);

      if (cachedStyle) {
        // Update the radius based on zoom level
        const img = cachedStyle.getImage();
        if (img instanceof Circle) {
          // Cast to Circle to access setRadius
          (img as Circle).setRadius(newRadius);
        }
        if (img instanceof RegularShape) {
          // @ts-expect-error
          (img as RegularShape).radius_ = newRadius;
        }

        // cachedStyle.getText()?.setText(sampleSize.toString())
        return cachedStyle
      }

      const fillColor = getSpeciesColor(speciesGroup);

/*
      const text = new Text({
        text: sampleSize.toString(),
        fill: new Fill({ color: '#000000' }),
        font: '10px sans-serif'
      });
*/

      let shape;

      if (sensitiveSpecies) {
        // triangle
        shape = new RegularShape({
          fill: new Fill({ color: fillColor }),
          stroke: new Stroke({ color: 'white', width: 1 }),
          points: 3,
          // radius: 5,
          radius: newRadius, // Adjust the radius based on zoom level
          // rotation: Math.PI / 4,
          angle: 0,
        });
      } else if (sampleSize > 1){
        // hexagon
        shape = new RegularShape({
          fill: new Fill({ color: fillColor }),
          stroke: new Stroke({ color: 'white', width: 1 }),
          points: 5,
          radius: newRadius, // Adjust the radius based on zoom level
          // radius: 5,
          // rotation: Math.PI / 4,
          angle: 0,
        });
      } else {
        // circle
        shape = new Circle({
          // radius: 5,
          radius: newRadius, // Adjust the radius based on zoom level
          fill: new Fill({ color: fillColor }),
          stroke: new Stroke({ color: 'white', width: 1 }),
        })
      }

      const style = new Style({image: shape});
      styleCache.set(cacheKey, style);
      return style
    }

    // Approximate center of Switzerland in EPSG:4326 (longitude, latitude)
    const centerLonLat = [8.2275, 46.9992]

    // Transform to 'EPSG:21781' projection
    const centerInSwissProjection = transform(
      centerLonLat as [number, number],
      'EPSG:4326',
      'EPSG:21781'
    )
    const createSwissTopoLayer = async () => {
      // Fetch WMTS capabilities from Swiss Topo service
      const wmtsCapabilitiesUrl = 'https://wmts.geo.admin.ch/1.0.0/WMTSCapabilities.xml'
      const response = await fetch(wmtsCapabilitiesUrl)
      const capabilitiesText = await response.text()
      const parser = new WMTSCapabilities()
      const capabilities = parser.read(capabilitiesText)

      // Define your desired layer and matrix set
      const layerIdentifier = 'ch.swisstopo.pixelkarte-farbe'
      const matrixSetIdentifier = '21781_27' // Correct matrix set based on capabilities

      // Find the layer and matrix set in the capabilities
      const layer = capabilities.Contents.Layer.find((l: any) => l.Identifier === layerIdentifier)
      const matrixSet = capabilities.Contents.TileMatrixSet.find(
        (ms: any) => ms.Identifier === matrixSetIdentifier
      )

      if (!layer) {
        throw new Error(`Layer "${layerIdentifier}" not found in WMTS capabilities.`)
      }
      if (!matrixSet) {
        throw new Error(`Matrix Set "${matrixSetIdentifier}" not found in WMTS capabilities.`)
      }

      const options = optionsFromCapabilities(capabilities, {
        layer: layerIdentifier,
        matrixSet: matrixSetIdentifier
      })

      if (!options) {
        throw new Error(
          `Failed to retrieve WMTS options for layer "${layerIdentifier}" and matrixSet "${matrixSetIdentifier}".`
        )
      }

      // Create WMTS layer for Swiss Topo background map
      const swissTopoLayer = new TileLayer({
        source: new WMTS(options)
      })
      swissTopoLayer.setOpacity(0.7)

      return swissTopoLayer
    }

    const setupMap = async () => {
      // Ensure the projection is correctly registered
      if (!swissProjection) {
        throw new Error('Swiss projection EPSG:21781 is not available.')
      }

      const swissTopoLayer = await createSwissTopoLayer()

      // Create CartoDB Positron layer for background map
      const cartoDBLayer = new TileLayer({
        source: new XYZ({
          url: 'https://{a-d}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png',
          attributions: '© OpenStreetMap contributors, © CARTO'
        })
      })
      // Create GBIF vector layer
      const gbifLayer = new VectorLayer({
        source: gbifVectorSource,
        visible: showGbifData.value
      })
      // Create our data vector layer
      const dataLayer = new VectorLayer({
        source: vectorSource,
        visible: showOurData.value
      })
      // Create the map view
      mapView.value = new View({
        center: centerInSwissProjection,
        projection: swissProjection,
        zoom: 8.7,
        minZoom: 8,
        maxZoom: 19
      }) as View

      // Create the map
      map.value = new Map({
        target: 'map',
        layers: [
          cartoDBLayer, // Use CartoDB Positron as the bottom layer
          swissTopoLayer,
          gbifLayer,
          dataLayer
        ],
        view: mapView.value as View,
        controls: []
      })

      // Watch for visibility state changes
      watch(showGbifData, (newValue) => {
        gbifLayer.setVisible(newValue)
        if (newValue && mStore.filteredMetadata) {
          fetchGbifDataFromStore()
        }
      })

      watch(showOurData, (newValue) => {
        dataLayer.setVisible(newValue)
      })
    }

    const setMapInteractivity = () => {
      map.value?.on('singleclick', (event) => {
        let clickedFeature: any = null

        // Find the feature that was clicked
        map.value?.forEachFeatureAtPixel(
          event.pixel,
          (feature) => {
            clickedFeature = feature
            return true // Stop at the first feature found
          },
          { hitTolerance: 5 }
        )

        if (clickedFeature) {
          if (clickedFeature.get('gbif_id')) {
            const scientificName = clickedFeature.get('scientific_name')
            const gbifId = clickedFeature.get('gbif_id')
            const originalCoords = clickedFeature.get('original_coords')

            // Log information to console as requested
            /*
            console.log({
              id: gbifId,
              coordinates: originalCoords,
              scientificName: scientificName
            });
*/
          } else {
            // First, remove the feature from the source
            vectorSource.removeFeature(clickedFeature)
            // Then add it back, which will put it at the top of the rendering order
            vectorSource.addFeature(clickedFeature)
            // Get the Gendib ID
            const gendibId = clickedFeature.get('gendib_sample_id')
            router.push({ params: { id: gendibId } })
          }
        }
      })

      // Update feature styles based on zoom level changes
      mapView.value?.on('change:resolution', () => {
        const zoom = mapView.value?.getZoom() || 8
        // Update main data points
        /*
        vectorSource.getFeatures().forEach((feature) => {
          const popId = feature.get('gendib_sample_id');
          const speciesGroup = mStore.speciesGroupMap.get(popId);
          const isSensitive = mStore.isSensitive(popId);
          feature.setStyle(getStyle(speciesGroup, zoom, isSensitive));
        });
        // Update GBIF data points
        gbifVectorSource.getFeatures().forEach((feature) => {
          const speciesGroup = feature.get('species_group');
          feature.setStyle(getGbifStyle(speciesGroup, zoom));
        });
*/
      })
    }

    // Wrapper function to call the GBIF service with all required parameters
    const fetchGbifDataFromStore = async () => {
      if (mStore.filteredMetadata && showGbifData.value) {
        // Get the active species filters from the store
        const speciesGroupFilter = mStore.activeFilters?.get('species_group')
        const speciesScientificFilter = mStore.activeFilters?.get('species_scientific')
        const zoom = mapView.value?.getZoom() || 8

        await fetchGbifData(
          mStore.filteredMetadata,
          gbifVectorSource,
          speciesGroupFilter,
          speciesScientificFilter,
          'EPSG:21781', // Swiss projection
          zoom
        )
      }
    }
    onMounted(async () => {
      await mStore.fetchMetadata()

      await setupMap()

      setMapInteractivity()
    })
    // Watch for changes in active filters to update GBIF data
    watch(
      [
        () => mStore.activeFilters?.get('species_group'),
        () => mStore.activeFilters?.get('species_scientific')
      ],
      () => {
        if (showGbifData.value) {
          fetchGbifDataFromStore()
        }
      },
      { deep: true }
    )
    watch(
      () => props.geoJSONCollection,
      (newValue) => {
        if (!newValue) return
        // Clear existing features
        vectorSource.clear()
        // Read features and transform coordinates
        const newFeatures: Feature<Geometry>[] = new GeoJSON().readFeatures(newValue, {
          dataProjection: 'EPSG:4326',
          featureProjection: 'EPSG:21781' // Transform to Swiss projection
        }) as Feature<Geometry>[]
        // Add features to the vector source
        vectorSource.addFeatures(newFeatures)
        // Style features
        const currentZoom = mapView.value?.getZoom() || 8 // Get initial zoom level, default to 8 if mapView is not set

        newFeatures.forEach((feature) => {
          const popId = feature.get('gendib_sample_id')
          const speciesGroup = mStore.speciesGroupMap.get(popId)
          const sampleSize = mStore.getSampleSize(popId);
          const isSensitive = mStore.isSensitive(popId);
          feature.setStyle(getStyle(speciesGroup, currentZoom, isSensitive, sampleSize))
        })
      },
      { immediate: true }
    )

    return {
      map,
      showGbifData,
      showOurData
    }
  }
})
</script>
